import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { Link } from 'react-router-dom'

import { PropsWithChildren, useState } from 'react'
import { TriangleAlert } from 'lucide-react'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import { Button } from '@/components/ui/button'
import TransferModal from 'app/frontend/base/pages/UserDashboard/components/TransferModal'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'

type MemberLinkProps = {
  className?: string
  member: wowaudit.TeamMembership
  compact?: boolean
  customSpecDetails?: wowaudit.TeamMembership['specDetails']
}

const MemberLink = ({
  className,
  member,
  compact,
  customSpecDetails,
  children,
}: PropsWithChildren<MemberLinkProps>) => {
  const [transferring, setTransferring] = useState(false)
  const specDetails = customSpecDetails ?? member.specDetails

  return (
    <span
      className={`inline-flex items-center justify-between ${className ?? ''}`}
    >
      <span
        className={`flex items-center min-w-0 ${compact ? 'gap-1.5' : 'gap-2'}`}
      >
        {specDetails ? (
          <Tooltip>
            <TooltipTrigger className='shrink-0'>
              <BlizzardIcon
                size={compact ? 'small' : 'medium'}
                iconName={specDetails.icon}
              />
            </TooltipTrigger>
            <TooltipContent>
              {specDetails.name} {member.characterReference.characterClass}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip>
            <TooltipTrigger className='shrink-0'>
              <CharacterClassIcon
                size={compact ? 'small' : 'medium'}
                characterClass={member.characterReference.characterClass}
              />
            </TooltipTrigger>
            <TooltipContent>
              {member.characterReference.characterClass}
            </TooltipContent>
          </Tooltip>
        )}
        <Link to={member.characterReference.path} className='truncate min-w-0'>
          {member.characterReference.name}
        </Link>

        {member.characterReference.gdprStatus !== 'exists' && (
          <HoverCard openDelay={150} closeDelay={150}>
            <HoverCardTrigger>
              <span className='text-destructive cursor-pointer'>
                <TriangleAlert size={14} />
              </span>
            </HoverCardTrigger>
            <HoverCardContent className='text-center'>
              <p>This character is not available in Blizzard&apos;s API</p>
              <ProtectedContent
                section='roster'
                requirement='admin'
                renderUnauthorized={false}
              >
                <Button
                  className='mt-3'
                  variant='secondary'
                  size='sm'
                  onClick={() => setTransferring(true)}
                >
                  Transfer
                </Button>
              </ProtectedContent>
            </HoverCardContent>
          </HoverCard>
        )}

        {transferring && (
          <TransferModal
            character={member.characterReference}
            setOpen={setTransferring}
          />
        )}
      </span>

      {children}
    </span>
  )
}

export default MemberLink
